import * as React from "react";
import { cn } from "../lib/utils";
import { Loader } from "./loader";

export interface LoadingStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The wait in words: "Laadin…" / "Loading…". The kit has no translations of its
   *  own, so the default is English; pass the page's own word. */
  label?: string;
  /** An optional second line under the label (what is loading, why it can take long). */
  hint?: string;
  /** `page` (default) stands in for a whole page, tab or panel: ringed spinner, label
   *  and a progress line. `inline` is one quiet row for a card, a list or a table cell. */
  variant?: "page" | "inline";
  /** How long this wait usually takes, in milliseconds. The progress line creeps
   *  towards 92 percent over this time and never claims to be done. */
  expectedMs?: number;
  /** The real share done (0 to 1) when the caller knows it. Replaces the creep. */
  progress?: number;
}

// The brand colour pulled a quarter towards the text colour, so the arc reads on
// both grounds even when a tenant's primary is close to the page ground.
const ARC_COLOR = "color-mix(in oklab, var(--color-primary) 75%, var(--color-foreground))";

/**
 * LoadingState - the one waiting screen for a page, a tab or a panel whose data is
 * still on its way. Same look as the account-free editor's splash
 * (`apps/web_site/src/editor/EditorSplash.tsx`): a ringed spinner, the label in one
 * size everywhere, and a thin progress line that shows the wait is moving.
 *
 * It fades in after a short delay (styles.css, `wa-loading-*`), so a fast load does
 * not flash it, and it keeps its place in the layout while it is invisible. The
 * progress line is an estimate unless `progress` is given: it moves fast at first,
 * slows down and stops at 92 percent, and the content replaces it when it is ready.
 * Under reduced motion the line is hidden, because a still line says nothing.
 */
export function LoadingState({
  label = "Loading…",
  hint,
  variant = "page",
  expectedMs = 12000,
  progress,
  className,
  style,
  ...props
}: LoadingStateProps) {
  if (variant === "inline") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-slot="loading-state"
        className={cn("wa-loading-in flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground", className)}
        style={style}
        {...props}
      >
        <Loader size="sm" aria-hidden style={{ color: ARC_COLOR }} />
        <span>{label}</span>
      </div>
    );
  }

  const known = typeof progress === "number";
  const share = known ? Math.min(1, Math.max(0, progress)) : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      data-slot="loading-state"
      className={cn(
        "wa-loading-in flex min-h-48 w-full flex-col items-center justify-center gap-4 px-6 py-12 text-center",
        className,
      )}
      style={style}
      {...props}
    >
      <span aria-hidden className="relative block size-8" style={{ color: ARC_COLOR }}>
        <svg viewBox="0 0 24 24" fill="none" className="absolute inset-0 size-8 text-foreground/10">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2} />
        </svg>
        <Loader size="md" className="absolute inset-0" />
      </span>

      <div className="flex flex-col items-center gap-1.5">
        <p className="m-0 text-base font-medium tracking-tight text-foreground">{label}</p>
        {hint && <p className="m-0 max-w-md text-sm text-muted-foreground">{hint}</p>}
      </div>

      <div
        aria-hidden
        data-loading-bar=""
        className="h-1 w-40 max-w-full overflow-hidden rounded-full bg-foreground/10"
      >
        <div
          className={cn("wa-loading-fill h-full rounded-full", !known && "wa-loading-creep")}
          style={
            known
              ? { transform: `scaleX(${share})` }
              : ({ "--wa-loading-ms": `${Math.max(1000, expectedMs)}ms` } as React.CSSProperties)
          }
        />
      </div>
    </div>
  );
}
